import { useEffect, useState, useCallback } from "react";
import { CalendarDays, Plus, Trash2, ChevronLeft, ChevronRight, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DatePicker from "@/components/DatePicker";
import { ACTIVITIES_START, PERIODS, PERIOD_LABELS } from "@/lib/constants";
import { Card, SectionHeader, PrimaryBtn, IconBtn, Field } from "./ui";

function GlobalCalendar({ slots }) {
  const now = new Date();
  const [view, setView] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const WEEK = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
  const DOT = { libre: "bg-emerald-400", reserve: "bg-sky-400", en_attente: "bg-amber-400", absence: "bg-slate-400", absence_attente: "bg-orange-400" };
  const pad = (n) => String(n).padStart(2, "0");
  const key = (d) => `${view.y}-${pad(view.m + 1)}-${pad(d)}`;
  const byDate = {};
  slots.forEach((s) => { (byDate[s.date] = byDate[s.date] || []).push(s); });
  const offset = (new Date(view.y, view.m, 1).getDay() + 6) % 7;
  const days = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const change = (delta) => { let m = view.m + delta, y = view.y; if (m < 0) { m = 11; y--; } else if (m > 11) { m = 0; y++; } setView({ y, m }); };
  return (
    <Card>
      <SectionHeader title="Vue d'ensemble des animateurs" />
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => change(-1)} data-testid="global-cal-prev" className="grid place-items-center w-9 h-9 rounded-full bg-slate-100 hover:bg-sky-100 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
        <span className="font-heading font-extrabold text-slate-900" data-testid="global-cal-title">{MONTHS[view.m]} {view.y}</span>
        <button onClick={() => change(1)} data-testid="global-cal-next" className="grid place-items-center w-9 h-9 rounded-full bg-slate-100 hover:bg-sky-100 transition-colors"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">{WEEK.map((w) => <div key={w} className="text-center text-[10px] font-bold text-slate-400">{w}</div>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />;
          const daySlots = byDate[key(d)] || [];
          return (
            <div key={d} className="aspect-square rounded-lg border border-slate-100 p-1 flex flex-col" data-testid={`global-cal-day-${key(d)}`}>
              <span className="text-[10px] font-semibold text-slate-500">{d}</span>
              <div className="flex flex-wrap gap-0.5 mt-auto">
                {daySlots.slice(0, 6).map((s) => <span key={s.id} title={`${s.animator_name} — ${s.status}`} className={`w-1.5 h-1.5 rounded-full ${DOT[s.status] || "bg-slate-300"}`} />)}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 mt-4 text-[11px] font-semibold text-slate-500">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /> Libre</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-sky-400" /> Réservé</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Dispo en attente</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-400" /> Absence en attente</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-slate-400" /> Absent</span>
      </div>
    </Card>
  );
}

export default function CalendarAdmin() {
  const [animators, setAnimators] = useState([]);
  const [slots, setSlots] = useState([]);
  const [newAnimator, setNewAnimator] = useState({ name: "", email: "", password: "", postal_codes: "" });
  const [slotForm, setSlotForm] = useState({ date: "", animator_id: "", status: "libre", period: "matin" });
  const [editAnim, setEditAnim] = useState(null);

  const load = useCallback(() => {
    api.get("/animators").then((r) => setAnimators(r.data)).catch(() => {});
    api.get("/admin/availability").then((r) => setSlots(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const parsePc = (s) => (s || "").split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

  const addAnimator = async () => {
    if (!newAnimator.name.trim() || !newAnimator.email.trim() || !newAnimator.password.trim()) {
      toast.error("Nom, email et mot de passe requis"); return;
    }
    try {
      await api.post("/animators", { name: newAnimator.name.trim(), email: newAnimator.email.trim(), password: newAnimator.password, color: "sky", postal_codes: parsePc(newAnimator.postal_codes) });
      setNewAnimator({ name: "", email: "", password: "", postal_codes: "" }); toast.success("Animateur créé (compte activé)"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delAnimator = async (a) => { await api.delete(`/animators/${a.id}`); toast.success("Animateur supprimé"); load(); };
  const saveEdit = async () => {
    try {
      await api.patch(`/animators/${editAnim.id}`, { name: editAnim.name, postal_codes: parsePc(editAnim.postal_codes) });
      toast.success("Animateur mis à jour"); setEditAnim(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addSlot = async (e) => {
    e.preventDefault();
    if (!slotForm.date || !slotForm.animator_id) { toast.error("Date et animateur requis"); return; }
    try {
      await api.post("/availability", slotForm);
      toast.success("Créneau ajouté"); setSlotForm({ date: "", animator_id: "", status: "libre", period: "matin" }); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const toggle = async (s) => { await api.patch(`/availability/${s.id}`, { status: s.status === "libre" ? "reserve" : "libre" }); load(); };
  const delSlot = async (s) => { await api.delete(`/availability/${s.id}`); load(); };
  const approve = async (s) => { await api.patch(`/availability/${s.id}/approve`); toast.success("Demande validée"); load(); };
  const reject = async (s) => { await api.patch(`/availability/${s.id}/reject`); toast.success("Demande refusée"); load(); };
  const fmt = (d) => new Date(d).toLocaleDateString("fr-BE", { weekday: "short", day: "numeric", month: "long", year: "numeric" });

  const pending = slots.filter((s) => s.status === "en_attente" || s.status === "absence_attente");
  const active = slots.filter((s) => s.status !== "en_attente" && s.status !== "absence_attente");

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-6">
        <Card>
          <h3 className="font-heading font-extrabold text-lg text-slate-900 mb-4">Animateurs</h3>
          <div className="space-y-2 mb-4" data-testid="admin-animators-list">
            {animators.map((a) => (
              <div key={a.id} className="flex items-center justify-between border border-slate-100 rounded-xl px-3 py-2">
                <div className="min-w-0">
                  <span className="font-semibold text-slate-700 block truncate">{a.name}</span>
                  {a.email && <span className="text-xs text-slate-400 block truncate">{a.email}</span>}
                  <span className="text-[11px] text-slate-400 block truncate">Zones : {(a.postal_codes || []).join(", ") || "aucune"}</span>
                </div>
                <div className="flex items-center gap-1">
                  <IconBtn icon={Pencil} color="sky" onClick={() => setEditAnim({ id: a.id, name: a.name, postal_codes: (a.postal_codes || []).join(", ") })} testid={`admin-edit-animator-${a.id}`} />
                  <IconBtn icon={Trash2} color="rose" onClick={() => delAnimator(a)} testid={`admin-del-animator-${a.id}`} />
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-2 border-t border-slate-100 pt-4">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Créer un compte animateur</p>
            <Input value={newAnimator.name} onChange={(e) => setNewAnimator({ ...newAnimator, name: e.target.value })} placeholder="Nom complet" className="rounded-xl" data-testid="admin-animator-name" />
            <Input type="email" value={newAnimator.email} onChange={(e) => setNewAnimator({ ...newAnimator, email: e.target.value })} placeholder="Email de connexion" className="rounded-xl" data-testid="admin-animator-email" />
            <Input type="text" value={newAnimator.password} onChange={(e) => setNewAnimator({ ...newAnimator, password: e.target.value })} placeholder="Mot de passe" className="rounded-xl" data-testid="admin-animator-password" />
            <Input type="text" value={newAnimator.postal_codes} onChange={(e) => setNewAnimator({ ...newAnimator, postal_codes: e.target.value })} placeholder="Codes postaux desservis (ex: 6000, 6040, 1348)" className="rounded-xl" data-testid="admin-animator-postal" />
            <PrimaryBtn onClick={addAnimator} data-testid="admin-add-animator" className="w-full justify-center"><Plus className="w-4 h-4" /> Créer</PrimaryBtn>
          </div>
        </Card>

        <Card>
          <h3 className="font-heading font-extrabold text-lg text-slate-900 mb-4">Nouveau créneau</h3>
          <form onSubmit={addSlot} className="space-y-4" data-testid="admin-slot-form">
            <Field label="Date"><DatePicker value={slotForm.date} onChange={(d) => setSlotForm({ ...slotForm, date: d })} testid="admin-slot-date" minDate={new Date(ACTIVITIES_START)} /></Field>
            <Field label="Animateur">
              <Select value={slotForm.animator_id} onValueChange={(v) => setSlotForm({ ...slotForm, animator_id: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="admin-slot-animator"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{animators.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Créneau">
              <Select value={slotForm.period} onValueChange={(v) => setSlotForm({ ...slotForm, period: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="admin-slot-period"><SelectValue /></SelectTrigger>
                <SelectContent>{PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Statut">
              <Select value={slotForm.status} onValueChange={(v) => setSlotForm({ ...slotForm, status: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="admin-slot-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="libre">Libre</SelectItem><SelectItem value="reserve">Réservé</SelectItem></SelectContent>
              </Select>
            </Field>
            <PrimaryBtn type="submit" data-testid="admin-slot-submit"><Plus className="w-4 h-4" /> Ajouter</PrimaryBtn>
          </form>
        </Card>
      </div>

      <div className="lg:col-span-2 space-y-6">
        <GlobalCalendar slots={slots} />
        {pending.length > 0 && (
          <Card>
            <SectionHeader title={`Demandes à valider (${pending.length})`} />
            <div className="space-y-2" data-testid="admin-pending-list">
              {pending.map((s) => (
                <div key={s.id} className="flex items-center gap-3 border border-amber-200 bg-amber-50 rounded-2xl p-3">
                  <span className="grid place-items-center w-10 h-10 rounded-xl bg-amber-100 text-amber-600"><CalendarDays className="w-5 h-5" /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 capitalize">{fmt(s.date)}</p>
                    <p className="text-xs text-slate-500">{s.animator_name} · {PERIOD_LABELS[s.period || "matin"]} · <span className={s.slot_type === "absence" ? "text-orange-600 font-semibold" : "text-emerald-600 font-semibold"}>{s.slot_type === "absence" ? "Absence" : "Disponibilité"}</span></p>
                  </div>
                  <button onClick={() => approve(s)} data-testid={`admin-approve-slot-${s.id}`} className="text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-colors">Valider</button>
                  <button onClick={() => reject(s)} data-testid={`admin-reject-slot-${s.id}`} className="text-xs font-bold px-3 py-1.5 rounded-full bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors">Refuser</button>
                </div>
              ))}
            </div>
          </Card>
        )}
        <Card>
          <SectionHeader title="Calendrier de disponibilité" />
          <div className="space-y-2" data-testid="admin-slots-list">
            {active.map((s) => (
              <div key={s.id} className="flex items-center gap-3 border border-slate-100 rounded-2xl p-3">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-sky-100 text-sky-600"><CalendarDays className="w-5 h-5" /></span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 capitalize">{fmt(s.date)}</p>
                  <p className="text-xs text-slate-500">{s.animator_name} · {PERIOD_LABELS[s.period || "matin"]}</p>
                </div>
                {s.status === "absence" ? (
                  <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-slate-200 text-slate-600">Absent</span>
                ) : (
                  <button
                    onClick={() => toggle(s)}
                    data-testid={`admin-slot-toggle-${s.id}`}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${s.status === "libre" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-sky-100 text-sky-700 hover:bg-sky-200"}`}
                  >
                    {s.status === "libre" ? "Libre" : "Réservé"}
                  </button>
                )}
                <IconBtn icon={Trash2} color="rose" onClick={() => delSlot(s)} testid={`admin-del-slot-${s.id}`} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Dialog open={!!editAnim} onOpenChange={(o) => !o && setEditAnim(null)}>
        <DialogContent className="max-w-md" data-testid="edit-animator-dialog">
          <DialogHeader><DialogTitle>Modifier l'animateur</DialogTitle></DialogHeader>
          {editAnim && (
            <div className="space-y-3">
              <Field label="Nom complet"><Input value={editAnim.name} onChange={(e) => setEditAnim({ ...editAnim, name: e.target.value })} className="rounded-xl" data-testid="edit-animator-name" /></Field>
              <Field label="Codes postaux desservis"><Input value={editAnim.postal_codes} onChange={(e) => setEditAnim({ ...editAnim, postal_codes: e.target.value })} placeholder="ex: 6000, 6040, 1348" className="rounded-xl" data-testid="edit-animator-postal" /></Field>
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditAnim(null)} className="text-sm font-bold px-4 py-2 rounded-full bg-slate-100 text-slate-700">Annuler</button>
                <PrimaryBtn onClick={saveEdit} data-testid="edit-animator-save">Enregistrer</PrimaryBtn>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
